function rgbChannelToHex(channel: number): string {
  return channel.toString(16).padStart(2, "0");
}

function normalizeHexColor(value: string): string | null {
  const trimmed = value.trim();

  if (/^#[0-9a-f]{6}$/i.test(trimmed)) {
    return trimmed.toLowerCase();
  }

  if (/^#[0-9a-f]{3}$/i.test(trimmed)) {
    const [r = "0", g = "0", b = "0"] = trimmed.slice(1).split("");
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }

  if (/^#[0-9a-f]{8}$/i.test(trimmed)) {
    return trimmed.slice(0, 7).toLowerCase();
  }

  if (/^#[0-9a-f]{4}$/i.test(trimmed)) {
    const [r = "0", g = "0", b = "0"] = trimmed.slice(1, 4).split("");
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }

  return null;
}

function rgbStringToHex(value: string): string | null {
  const match = value.match(
    /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*(?:0|1|0?\.\d+))?\s*\)$/i,
  );
  if (!match) {
    return null;
  }

  const channels = match.slice(1, 4).map((part) => {
    const parsed = Number.parseInt(part ?? "0", 10);
    return Number.isNaN(parsed) ? 0 : Math.min(Math.max(parsed, 0), 255);
  });

  return `#${channels.map(rgbChannelToHex).join("")}`;
}

export function normalizeColorInputValue(
  value: string | null | undefined,
  fallback = "#000000",
): string {
  const trimmed = value?.trim();
  if (!trimmed) {
    return fallback;
  }

  const normalizedHex = normalizeHexColor(trimmed);
  if (normalizedHex) {
    return normalizedHex;
  }

  const normalizedRgb = rgbStringToHex(trimmed);
  if (normalizedRgb) {
    return normalizedRgb;
  }

  if (
    trimmed.startsWith("var(")
    && trimmed.endsWith(")")
    && typeof window !== "undefined"
  ) {
    const tokenName = trimmed.slice(4, -1).trim();
    const resolvedValue = getComputedStyle(document.documentElement)
      .getPropertyValue(tokenName)
      .trim();

    if (resolvedValue) {
      return normalizeColorInputValue(resolvedValue, fallback);
    }
  }

  return fallback;
}
